/**
 * Shared AI Model Types
 *
 * Provides consistent model definitions across frontend and backend
 * with a clean API for referencing models: AI_MODELS.GPT_4O, etc.
 */

// Model identifier constants with clean naming
export const AI_MODELS = {
  GPT_4O: "gpt-4o",
  GPT_5: "gpt-5",
  CLAUDE_4_SONNET: "claude-4-sonnet-20250514",
  CLAUDE_OPUS_4_1: "claude-opus-4-1-20250805",
} as const;

// Derive the union type from the constants
export type SupportedModelId = (typeof AI_MODELS)[keyof typeof AI_MODELS];

// Model option interface for UI components
export interface AiModelOption {
  id: SupportedModelId;
  display: string;
  label: string;
  value: SupportedModelId;
}

// Model options array for dropdowns/selectors
export const AI_MODEL_OPTIONS: readonly AiModelOption[] = [
  {
    id: AI_MODELS.GPT_4O,
    display: "GPT-4o",
    label: "GPT-4o - Balanced speed and accuracy for most transformations",
    value: AI_MODELS.GPT_4O,
  },
  {
    id: AI_MODELS.GPT_5,
    display: "GPT-5",
    label:
      "GPT-5 - Slower, Best for precise, multi-step workflows; follows detailed instructions exactly",
    value: AI_MODELS.GPT_5,
  },
  {
    id: AI_MODELS.CLAUDE_4_SONNET,
    display: "Claude 4 Sonnet",
    label:
      "Claude 4 Sonnet - Best for broad or ambiguous requests; turns unclear goals into correct workflows",
    value: AI_MODELS.CLAUDE_4_SONNET,
  },
  {
    id: AI_MODELS.CLAUDE_OPUS_4_1,
    display: "Claude Opus 4.1",
    label:
      "Claude Opus 4.1 - Advanced reasoning and coding capabilities with extended context",
    value: AI_MODELS.CLAUDE_OPUS_4_1,
  },
] as const;

// Array of just the model IDs for validation schemas
export const ALLOWED_PUBLIC_MODELS: readonly SupportedModelId[] = [
  AI_MODELS.GPT_4O,
  AI_MODELS.GPT_5,
  AI_MODELS.CLAUDE_4_SONNET,
  AI_MODELS.CLAUDE_OPUS_4_1,
] as const;

// Provider mapping type
export type ModelProvider = "openai" | "claude";

// Model to provider mappings
export const MODEL_PROVIDER_MAPPINGS: Record<SupportedModelId, ModelProvider> =
  {
    [AI_MODELS.GPT_4O]: "openai",
    [AI_MODELS.GPT_5]: "openai",
    [AI_MODELS.CLAUDE_4_SONNET]: "claude",
    [AI_MODELS.CLAUDE_OPUS_4_1]: "claude",
  } as const;

// Helper functions
export function getModelProvider(modelId: SupportedModelId): ModelProvider {
  return MODEL_PROVIDER_MAPPINGS[modelId];
}

export function isClaudeModel(modelId: SupportedModelId): boolean {
  return getModelProvider(modelId) === "claude";
}

export function isOpenAIModel(modelId: SupportedModelId): boolean {
  return getModelProvider(modelId) === "openai";
}

export function getModelOption(
  modelId: SupportedModelId,
): AiModelOption | undefined {
  return AI_MODEL_OPTIONS.find((option) => option.id === modelId);
}
